// client/src/pages/PTO/PTOHeatMap.tsx
// Employee x Month heat map grid showing PTO day counts with color intensity.

import React, { useMemo } from 'react';
import type { PTOEntry, PTOEmployee, EntryType } from './ptoTypes';
import { OFFICES } from './ptoConstants';
import { getHeatLevel, getOfficeColor } from './ptoUtils';

// ─── Props ──────────────────────────────────────────────────────────────────

interface PTOHeatMapProps {
  entries: PTOEntry[];
  employees: PTOEmployee[];
  onEmployeeClick: (emp: PTOEmployee) => void;
  year?: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Entry types that count toward PTO in the heat map. */
const PTO_TYPES: Set<EntryType> = new Set([
  'PTO',
  'Half Day',
  'Sabbatical',
  'Tentative PTO',
]);

const HEAT_COLORS = [
  'transparent',
  'rgba(122,162,255,.12)',
  'rgba(122,162,255,.25)',
  'rgba(122,162,255,.40)',
  'rgba(122,162,255,.60)',
];

// ─── Component ──────────────────────────────────────────────────────────────

const PTOHeatMap: React.FC<PTOHeatMapProps> = ({
  entries,
  employees,
  onEmployeeClick,
  year = 2026,
}) => {
  // Filter entries to PTO types for the target year
  const ptoEntries = useMemo(() => {
    const yearStr = String(year);
    return entries.filter(
      (e) => PTO_TYPES.has(e.entryType) && e.entryDate.startsWith(yearStr),
    );
  }, [entries, year]);

  // Build staffIndex -> month -> days
  const daysByStaffMonth = useMemo(() => {
    const m = new Map<string, number[]>(); // staffIndex -> [12 months]
    for (const e of ptoEntries) {
      const month = Number(e.entryDate.slice(5, 7)) - 1; // 0-indexed
      const hours = e.hours ?? 8;
      const days = hours / 8;
      let arr = m.get(e.staffIndex);
      if (!arr) {
        arr = new Array(12).fill(0);
        m.set(e.staffIndex, arr);
      }
      arr[month] += days;
    }
    return m;
  }, [ptoEntries]);

  // Group employees by office (only offices that appear in OFFICES, order preserved)
  const officeGroups = useMemo(() => {
    const groups: { office: string; color: string; emps: PTOEmployee[] }[] = [];
    for (const o of OFFICES) {
      const emps = employees
        .filter((e) => e.office === o.value)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName));
      if (emps.length > 0) {
        groups.push({ office: o.value, color: o.color, emps });
      }
    }
    return groups;
  }, [employees]);

  function getMonthDays(staffIndex: string, month: number): number {
    const arr = daysByStaffMonth.get(staffIndex);
    if (!arr) return 0;
    return Math.round(arr[month] * 10) / 10; // 1 decimal
  }

  function getTotal(staffIndex: string): number {
    const arr = daysByStaffMonth.get(staffIndex);
    if (!arr) return 0;
    const sum = arr.reduce((a, b) => a + b, 0);
    return Math.round(sum * 10) / 10;
  }

  function getGroupMonthTotal(emps: PTOEmployee[], month: number): number {
    let total = 0;
    for (const emp of emps) {
      total += getMonthDays(emp.staffIndex, month);
    }
    return Math.round(total * 10) / 10;
  }

  function getGroupTotal(emps: PTOEmployee[]): number {
    let total = 0;
    for (const emp of emps) {
      total += getTotal(emp.staffIndex);
    }
    return Math.round(total * 10) / 10;
  }

  const colStyle: React.CSSProperties = {
    padding: '6px 8px',
    textAlign: 'center',
    fontSize: 12,
    borderBottom: '1px solid var(--border)',
  };

  return (
    <div className="card">
      <div
        className="hd"
        style={{
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>PTO Heat Map &mdash; {year}</span>

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted)' }}>
          <span>Less</span>
          {HEAT_COLORS.map((bg, i) => (
            <div
              key={i}
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                background: i === 0 ? 'rgba(255,255,255,.04)' : bg,
                border: '1px solid rgba(255,255,255,.08)',
              }}
            />
          ))}
          <span>More</span>
        </div>
      </div>

      <div className="bd" style={{ overflowX: 'auto', padding: 0 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: 800,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  ...colStyle,
                  textAlign: 'left',
                  fontWeight: 700,
                  fontSize: 11,
                  color: 'var(--muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '.5px',
                  position: 'sticky',
                  left: 0,
                  background: 'var(--bg)',
                  zIndex: 1,
                  minWidth: 160,
                }}
              >
                Employee
              </th>
              {MONTH_LABELS.map((label) => (
                <th
                  key={label}
                  style={{
                    ...colStyle,
                    fontWeight: 700,
                    fontSize: 11,
                    color: 'var(--muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '.5px',
                    minWidth: 48,
                  }}
                >
                  {label}
                </th>
              ))}
              <th
                style={{
                  ...colStyle,
                  fontWeight: 700,
                  fontSize: 11,
                  color: 'var(--muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '.5px',
                  minWidth: 52,
                }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {officeGroups.map(({ office, color, emps }) => (
              <React.Fragment key={office}>
                {/* Office header row */}
                <tr>
                  <td
                    colSpan={14}
                    style={{
                      padding: '8px 10px',
                      fontWeight: 700,
                      fontSize: 12,
                      background: `${color}15`,
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 700,
                        background: `${color}33`,
                        color,
                      }}
                    >
                      {office}
                    </span>
                  </td>
                </tr>

                {/* Employee rows */}
                {emps.map((emp) => {
                  const total = getTotal(emp.staffIndex);
                  return (
                    <tr key={emp.staffIndex}>
                      <td
                        style={{
                          ...colStyle,
                          textAlign: 'left',
                          position: 'sticky',
                          left: 0,
                          background: 'var(--bg)',
                          zIndex: 1,
                        }}
                      >
                        <button
                          onClick={() => onEmployeeClick(emp)}
                          style={{
                            all: 'unset',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text)',
                          }}
                          onMouseEnter={(e) => {
                            (e.target as HTMLElement).style.color = 'var(--accent)';
                          }}
                          onMouseLeave={(e) => {
                            (e.target as HTMLElement).style.color = 'var(--text)';
                          }}
                        >
                          {emp.displayName}
                        </button>
                      </td>
                      {Array.from({ length: 12 }, (_, month) => {
                        const days = getMonthDays(emp.staffIndex, month);
                        const level = getHeatLevel(days);
                        return (
                          <td
                            key={month}
                            style={{
                              ...colStyle,
                              background: HEAT_COLORS[level],
                              color: days > 0 ? 'var(--text)' : 'rgba(167,179,196,.3)',
                              fontWeight: days > 0 ? 600 : 400,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {days > 0 ? days : '--'}
                          </td>
                        );
                      })}
                      <td
                        style={{
                          ...colStyle,
                          fontWeight: 700,
                          color: total > 0 ? 'var(--accent)' : 'var(--muted)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {total > 0 ? total : '--'}
                      </td>
                    </tr>
                  );
                })}

                {/* Office subtotal row */}
                <tr>
                  <td
                    style={{
                      ...colStyle,
                      textAlign: 'left',
                      fontWeight: 700,
                      fontSize: 11,
                      color: 'var(--muted)',
                      position: 'sticky',
                      left: 0,
                      background: 'var(--bg)',
                      zIndex: 1,
                      borderBottom: '2px solid var(--border)',
                    }}
                  >
                    {office} Total
                  </td>
                  {Array.from({ length: 12 }, (_, month) => {
                    const total = getGroupMonthTotal(emps, month);
                    return (
                      <td
                        key={month}
                        style={{
                          ...colStyle,
                          fontWeight: 700,
                          fontSize: 11,
                          color: total > 0 ? 'var(--text)' : 'var(--muted)',
                          background: 'rgba(255,255,255,.02)',
                          borderBottom: '2px solid var(--border)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {total > 0 ? total : '--'}
                      </td>
                    );
                  })}
                  <td
                    style={{
                      ...colStyle,
                      fontWeight: 700,
                      fontSize: 11,
                      color: 'var(--accent)',
                      background: 'rgba(255,255,255,.02)',
                      borderBottom: '2px solid var(--border)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {getGroupTotal(emps) || '--'}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PTOHeatMap;
